import { useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useLocation } from "react-router-dom";

import { getDetails } from "../../services/api";
import { useAuth } from "../../contexts/auth";
import { isDisconnected } from "../../utils/readingInterval";
import {
  getDate,
  getTime,
  configurePoint,
} from "./configurations/detailsConfig";

import Modal from "../../components/Modal";
import Chart from "./Chart";
import Info from "./Info";
import Header from "./Header";

const Details = () => {
  const location = useLocation();
  const point = location.state;
  const pointType = point?.pointType;
  const { logout, setLoginFailed, setLoginFailedMessage } = useAuth();

  const [readingType, setReadingType] = useState([]);
  const [unit, setUnit] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [lastReading, setLastReading] = useState();
  const [dateTime, setDateTime] = useState();
  const [manualPumpDrive, setManualPumpDrive] = useState();
  const [automaticPumpDrive, setAutomaticPumpDrive] = useState({
    enabled: null,
  });
  const [loading, setLoading] = useState(true);
  const [disconnected, setDisconnected] = useState(false);

  const handleSetDetails = useCallback(
    (data) => {
      if (!data) {
        return;
      }

      const pumpDrive = data.pumpDrive;
      const lastReading = data.lastReading;

      if (lastReading) {
        const readings = Object.values(lastReading.readings || {});
        setChartData(data.chartData || []);
        setDateTime(
          `${getDate(lastReading?.dateTime)} ${getTime(lastReading?.dateTime)}`
        );
        setLastReading(readings);

        if (pumpDrive) {
          setManualPumpDrive(pumpDrive.manual);
          setAutomaticPumpDrive(pumpDrive.automatic || { enabled: null });
        }

        if (data.deviceType && point) {
          point.deviceType = data.deviceType;
        }

        if (point && lastReading.dateTime) {
          point.dateTime = lastReading.dateTime;
        }
      }
    },
    [point]
  );

  const handleGetDetails = useCallback(async () => {
    if (!point || !point.dateTime || !pointType) {
      return;
    }

    if (!isDisconnected(point.dateTime)) {
      try {
        const pointId = point.pointId ? String(point.pointId) : null;
        const type = pointType ? String(pointType) : null;

        const response = await getDetails(pointId, type, point.dateTime);

        if (!response) {
          return;
        }

        // Mensagens de erro que requerem logout (apenas autenticação)
        const authErrorMessages = ["Expired token", "Connection timed out"];

        // Verificar se response.data é uma string de erro de autenticação
        if (typeof response.data === "string") {
          // Verificar se é erro de autenticação (requer logout)
          const isAuthError = authErrorMessages.some((errorMsg) =>
            response.data.includes(errorMsg)
          );

          if (isAuthError) {
            setLoginFailed(true);
            setLoginFailedMessage(response.data);
            logout();
            return;
          }

          // Verificar outros erros (não fazem logout)
          if (
            response.data.startsWith("Wrong") ||
            response.data.includes("Wrong number")
          ) {
            return;
          }

          // Tentar fazer parse apenas se parecer JSON válido
          if (
            response.data.trim().startsWith("{") ||
            response.data.trim().startsWith("[")
          ) {
            try {
              const parsed = JSON.parse(response.data);
              handleSetDetails(parsed);
              return;
            } catch (e) {
              return;
            }
          }
        } else if (
          typeof response.data === "object" &&
          response.data !== null
        ) {
          // Se for objeto, processar diretamente
          handleSetDetails(response.data);
        }
      } catch (error) {
        console.error("Erro ao buscar detalhes:", error);
        if (error?.response?.status === 401) {
          logout();
        }
      }
    } else {
      setDateTime(`${getDate(point.dateTime)} ${getTime(point.dateTime)}`);
      setDisconnected(true);
    }
  }, [
    point,
    pointType,
    handleSetDetails,
    logout,
    setLoginFailed,
    setLoginFailedMessage,
  ]);

  useEffect(() => {
    if (!point || !pointType) {
      return;
    }

    configurePoint(pointType, setReadingType, setUnit);
    handleGetDetails();
    const intervalId = setInterval(handleGetDetails, 1000 * 30);
    return () => clearInterval(intervalId);
  }, [point, pointType, handleGetDetails]);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 3000);
  }, []);

  if (!point) {
    return (
      <Container>
        <Row className="justify-content-center">
          <Col md={7} lg={5} className="text-center mt-5">
            <p>Erro: Dados do ponto não encontrados</p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      {loading && <Modal />}

      <Container>
        <Row className="justify-content-center">
          <Col
            md={7}
            xl={9}
            className={`border ${
              point?.alarm
                ? "border-danger bg-color-alarm"
                : "border-primary bg-color"
            } border-2 rounded p-0 shadow`}
            style={{ height: "99.98vh" }}
          >
            <Header point={point} dateTime={dateTime} />

            <Info
              point={point}
              lastReading={lastReading}
              readingType={readingType}
              unit={unit}
              disconnected={disconnected}
              manualPumpDrive={manualPumpDrive}
              automaticPumpDrive={automaticPumpDrive}
            />

            <Chart chartData={chartData} pointType={pointType} />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Details;
